import { useCallback, useRef, useState } from 'react';
import { CURSOR_PAGE_SIZE, type CursorPageCache } from 'lib/cursor-pagination';

type FetchCursorPage<T> = (
  cursor: number | null
) => Promise<{
  items: T[];
  nextCursor: number | null;
  hasMore: boolean;
  total?: number;
  totalPages?: number;
}>;

export function useCursorListPage<T>(fetchPage: FetchCursorPage<T>, limit = CURSOR_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const cacheRef = useRef<Map<number, CursorPageCache<T>>>(new Map());
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const applyCache = useCallback((pageNum: number, cached: CursorPageCache<T>) => {
    setItems(cached.items);
    setHasMore(cached.hasMore);
    setPage(pageNum);
  }, []);

  const applyTotals = useCallback((result: { total?: number; totalPages?: number }) => {
    if (result.total != null) setTotal(result.total);
    if (result.totalPages != null) setTotalPages(Math.max(1, result.totalPages));
    else if (result.total != null) {
      setTotalPages(Math.max(1, Math.ceil(result.total / limit)));
    }
  }, [limit]);

  const fetchAndStore = useCallback(async (pageNum: number, cursor: number | null) => {
    const result = await fetchRef.current(cursor);
    cacheRef.current.set(pageNum, result);
    applyTotals(result);
    return result;
  }, [applyTotals]);

  const loadPage = useCallback(
    async (pageNum: number, options: { silent?: boolean } = {}) => {
      const { silent = false } = options;
      if (pageNum < 1) pageNum = 1;

      const cached = cacheRef.current.get(pageNum);
      if (cached) {
        applyCache(pageNum, cached);
        return;
      }

      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        let current = 0;
        const cachedPages = [...cacheRef.current.keys()];
        if (cachedPages.length > 0) current = Math.max(...cachedPages);

        if (current === 0) {
          await fetchAndStore(1, null);
          current = 1;
        }

        while (current < pageNum) {
          const prev = cacheRef.current.get(current);
          if (!prev?.hasMore || prev.nextCursor == null) break;
          current += 1;
          await fetchAndStore(current, prev.nextCursor);
        }

        const target = cacheRef.current.get(pageNum);
        if (target) applyCache(pageNum, target);
        else if (pageNum === 1 && cacheRef.current.has(1)) {
          applyCache(1, cacheRef.current.get(1)!);
        } else {
          setItems([]);
          setHasMore(false);
          setPage(pageNum);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [applyCache, fetchAndStore]
  );

  const resetAndLoad = useCallback(() => {
    cacheRef.current.clear();
    setPage(1);
    setTotal(0);
    setTotalPages(1);
    return loadPage(1);
  }, [loadPage]);

  const refreshCurrent = useCallback(() => {
    cacheRef.current.delete(page);
    return loadPage(page, { silent: true });
  }, [loadPage, page]);

  const goFirst = useCallback(() => loadPage(1), [loadPage]);
  const goPrev = useCallback(() => {
    if (page > 1) loadPage(page - 1);
  }, [loadPage, page]);
  const goNext = useCallback(() => {
    if (page < totalPages || hasMore) loadPage(page + 1);
  }, [hasMore, loadPage, page, totalPages]);
  const goToPage = useCallback(
    (target: number) => {
      if (target < 1) return;
      if (total > 0 && target > totalPages) return;
      loadPage(target);
    },
    [loadPage, total, totalPages]
  );
  const goLast = useCallback(() => {
    if (total > 0 && totalPages > 0) loadPage(totalPages);
  }, [loadPage, total, totalPages]);

  return {
    page,
    items,
    loading,
    refreshing,
    hasMore,
    hasPrev: page > 1,
    total,
    totalPages,
    loadPage,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goPrev,
    goNext,
    goToPage,
    goLast,
    limit,
  };
}
